import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { workshopLibrary } from '../data/workshopLibrary';
import { motion, AnimatePresence } from 'framer-motion';

const activeWorkshops = [
  {
    id: 101,
    title: "Verksted: Psykologisk Trygghet",
    client: "TechCorp AS",
    department: "Utviklingsavdelingen",
    date: "I dag, 13:00 - 15:00",
    status: "live",
    participants: 42
  }
];

export const Workshops = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Alle");

  const categories = ["Alle", ...new Set(workshopLibrary.map(w => w.category))];

  const filteredWorkshops = workshopLibrary.filter(w => {
    const matchesSearch = w.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "Alle" || w.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Workshop Engine</h1>
          <p className="text-slate-500 mt-1">Fasiliter digitale prosesser med anonym deltakelse og sanntidsdata.</p>
        </div>
        <Button variant="primary" icon={<SafeIcon name="PlayCircle" />}>
          Start Tom Workshop
        </Button>
      </div>

      {/* Active Workshops Section */}
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Aktive Sesjoner</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {activeWorkshops.map((ws) => (
            <Card key={ws.id} className="border-l-4 border-l-rose-500">
              <CardContent>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                      LIVE NÅ
                    </span>
                    <h3 className="text-xl font-bold text-slate-900">{ws.title}</h3>
                    <p className="text-sm text-slate-500">{ws.client} • {ws.department}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-1.5 text-slate-600 font-medium font-mono">
                      <SafeIcon name="Users" /> {ws.participants}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="primary" className="flex-1" icon={<SafeIcon name="Monitor" />}>Gå til Panel</Button>
                  <Button variant="secondary" icon={<SafeIcon name="Share2" />}>Inviter</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Library Section */}
      <section className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-t border-slate-200 pt-8">
          <h2 className="text-xl font-bold text-slate-900">Workshop Bibliotek</h2>
          
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <SafeIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder="Søk i bibliotek..." 
                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <AnimatePresence>
            {filteredWorkshops.map((ws, index) => (
              <motion.div
                key={ws.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Card hover={true} className="h-full flex flex-col">
                  <CardContent className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                        <SafeIcon name={ws.icon} />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{ws.category}</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2 leading-tight">{ws.title}</h4>
                    <p className="text-xs text-slate-500 line-clamp-3">{ws.description}</p>
                  </CardContent>
                  <div className="px-6 py-3 bg-slate-50/50 border-t border-slate-100 mt-auto">
                    <button className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 group">
                      Opprett prosjekt <SafeIcon name="ArrowRight" className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        
        {filteredWorkshops.length === 0 && (
          <div className="text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
            <div className="text-slate-400 mb-2 text-4xl flex justify-center">
              <SafeIcon name="Search" />
            </div>
            <p className="text-slate-500">Ingen workshoper samsvarer med søket ditt.</p>
          </div>
        )}
      </section>
    </div>
  );
};